<script lang="ts" setup>
usePageSeo('seoHikingTrailsTitle', 'seoHikingTrailsDescription');

const trails = [
  {
    id: 0,
    header: 'anavasiMapheader',
    details: 'anavasiMapDetails',
    difficulty: 'difficultyModerate',
    time: '4-5h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336662/refuge/hiking-trails/%CE%A7%CE%AC%CF%81%CF%84%CE%B7%CF%82_nirv2c.jpg"
  },
  {
    id: 1,
    header: 'katarraktisKatafidiHeader',
    details: 'katarraktisKatafidiDetails',
    difficulty: 'difficultyChallenging',
    time: '3h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336636/refuge/hiking-trails/146863770_1867225130118900_6626241063132505035_n_aijc1c.png"
  },
  {
    id: 2,
    header: 'katarraktisMpresenaKatafidiHeader',
    details: 'katarraktisMpresenaKatafidiDetails',
    difficulty: 'difficultyChallenging',
    time: '5h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336630/refuge/hiking-trails/145635627_447572932962592_6030161274809985155_n_w4ils0.png"
  },
  {
    id: 3,
    header: 'katarraktisAgiosKosmasHeader',
    details: 'katarraktisAgiosKosmasDetails',
    difficulty: 'difficultyEasy',
    time: '2h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336628/refuge/hiking-trails/146137810_1555609857963597_1088007968453264047_n_lcmt8c.png"
  },
  {
    id: 4,
    header: 'katafygioKatarraktesHeader',
    details: 'katafygioKatarraktesDetails',
    difficulty: 'difficultyModerate',
    time: '2.5h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336628/refuge/hiking-trails/145714701_230561195362302_6194424169469426324_n_ab2bxy.png"
  },
  {
    id: 5,
    header: 'katarraktesKatafygioHeader',
    details: 'katarraktesKatafygioDetails',
    difficulty: 'difficultyModerate',
    time: '3h',
    image: "https://res.cloudinary.com/dlct0dnwt/image/upload/v1668336628/refuge/hiking-trails/146587805_1630010727413364_3522811265685613891_n_aejbhi.png"
  }
]
</script>

<template>
  <div class="hiking-page">
    <Hero 
      :title="$t('Hiking')" 
      :subtitle="$t('hikingHeader')"
      backgroundImage="https://res.cloudinary.com/dlct0dnwt/image/upload/v1743292009/refuge/2025/6R2B7757_nhmjoi.jpg"
    />

    <div class="content-container">
      <div class="intro-box">
        <p>{{ $t('hikingParagraph') }}</p>
      </div>

      <div class="trails-grid">
        <a v-for="trail in trails" :id="`trail-${trail.id}`" :key="trail.id" class="trail-card"
           :href="trail.image" target="_blank" rel="noopener noreferrer">
          <div class="card-image-box">
            <nuxt-img :src="trail.image" :alt="$t(trail.header)" format="webp" loading="lazy" />
            <div class="image-overlay">
              <Icon name="ph:magnifying-glass-plus-bold" size="2rem" aria-hidden="true"/>
            </div>
          </div>
          <div class="card-body">
            <div class="card-meta">
              <span class="difficulty" :class="trail.difficulty.toLowerCase().replace('difficulty', '')">{{ $t(trail.difficulty) }}</span>
              <span class="duration">
                <Icon name="ph:clock-bold" />
                {{ trail.time }}
              </span>
            </div>
            <h3 class="trail-title">{{ $t(trail.header) }}</h3>
            <p class="trail-desc">{{ $t(trail.details) }}</p>
            <span class="sr-only">{{ $t('viewTrailPhoto') }} ({{ $t('opensInNewTab') }})</span>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.hiking-page
  width: 100%

.content-container
  max-width: 1100px
  margin: 0 auto
  padding: 4rem 2rem 8rem

.intro-box
  text-align: center
  max-width: 800px
  margin: 0 auto 6rem
  font-size: 1.15rem
  line-height: 1.8
  color: rgba(0, 0, 0, 0.7)

.dark-mode .intro-box
  color: rgba(255, 255, 255, 0.7)

// Trails Grid
.trails-grid
  display: grid
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr))
  gap: 2.5rem

.trail-card
  background-color: white
  color: inherit
  text-decoration: none
  border-radius: 8px
  overflow: hidden
  box-shadow: $shadow-modern
  transition: all $transition-standard
  cursor: pointer
  display: flex
  flex-direction: column

  &:hover
    transform: translateY(-8px)
    box-shadow: $shadow-hover

    .image-overlay
      opacity: 1
    
    .card-image-box img
      transform: scale(1.05)

.dark-mode .trail-card
  background-color: $color-darker-bg

.card-image-box
  height: 220px
  position: relative
  overflow: hidden

  img
    width: 100%
    height: 100%
    object-fit: cover
    transition: transform 0.6s ease

.image-overlay
  position: absolute
  inset: 0
  background-color: rgba(0, 0, 0, 0.3)
  display: flex
  align-items: center
  justify-content: center
  color: white
  opacity: 0
  transition: opacity $transition-fast

.card-body
  padding: 1.5rem
  display: flex
  flex-direction: column
  gap: 0.75rem
  flex: 1

.card-meta
  display: flex
  justify-content: space-between
  align-items: center

.difficulty
  font-size: 0.7rem
  font-weight: 800
  text-transform: uppercase
  letter-spacing: 0.5px
  padding: 0.25rem 0.5rem
  border-radius: 4px
  
  &.easy
    background-color: #e6f7ef
    color: #287b5d
  &.moderate
    background-color: #fff4e6
    color: #b45d00
  &.challenging
    background-color: #fee6e6
    color: #c53030

.duration
  display: flex
  align-items: center
  gap: 0.4rem
  font-size: 0.8rem
  font-weight: 600
  opacity: 0.5

.trail-title
  font-size: 1.25rem
  font-weight: 700
  margin: 0
  line-height: 1.2

.trail-desc
  font-size: 0.95rem
  line-height: 1.6
  opacity: 0.7
  margin: 0

@media (max-width: 768px)
  .trails-grid
    grid-template-columns: 1fr
</style>
